import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { ChevronRight, Search, Send, Users } from "lucide-react";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Checkbox } from "../../../components/ui/checkbox";
import { Badge } from "../../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { cn } from "../../../components/ui/utils";
import { useMediaQuery } from "@mui/material";
import SingleSelectNew from "../../../components/SelectField/SingleSelectNew";
import moment from "moment";

const EmployeeTab = ({employees, rawAppointments, clients, dateRange, setShowMessageModal, setMessageRecipients, setSelectedEmployeeIds, selectedEmployeeIds, setShowEmployeeDetailModal, setSelectedEmployee, setEmployeeHistoryTimeFrame}) => {
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [employeeStatusFilter, setEmployeeStatusFilter] = useState("all");
  const isMobile = useMediaQuery("(max-width:767px)");

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "missing", label: "Missing Proof" },
    { value: "late", label: "Late Proof" },
    { value: "complete", label: "Complete" },
  ];

  const employeeRiskData = useMemo(() => {
    if (!employees?.length || !rawAppointments?.length) return [];

    const now = moment();

    let rangeStartDate, rangeEndDate;

    switch (dateRange || "today") {
      case "today":
        rangeStartDate = now.format("YYYY-MM-DD");
        rangeEndDate   = now.format("YYYY-MM-DD");
        break;
      case "yesterday":
        const yesterday = moment().subtract(1, "day");
        rangeStartDate = yesterday.format("YYYY-MM-DD");
        rangeEndDate   = yesterday.format("YYYY-MM-DD");
        break;
      case "last7":
        rangeStartDate = moment().subtract(7, "days").format("YYYY-MM-DD");
        rangeEndDate   = now.format("YYYY-MM-DD");
        break;
      case "last30":
      default:
        rangeStartDate = moment().subtract(30, "days").format("YYYY-MM-DD");
        rangeEndDate   = now.format("YYYY-MM-DD");
        break;
    }

    const clientMap = clients?.reduce((acc, c) => {
      acc[c.id] = c?.clientName || c?.firstName;
      return acc;
    }, {}) || {};

    const appointmentsByUser = {};

    rawAppointments?.forEach((appt) => {
      const apptDateStr = moment(appt?.appointmentDateTime).format("YYYY-MM-DD");

      if (apptDateStr < rangeStartDate || apptDateStr > rangeEndDate) {
        return;
      }

      if (!appointmentsByUser[appt.userId]) {
        appointmentsByUser[appt.userId] = [];
      }
      appointmentsByUser[appt.userId].push(appt);
    });

    const result = employees?.map((emp) => {
      const empAppointments = appointmentsByUser[emp?.id] || [];

      let missing = 0;
      let late = 0;
      let invalid = 0;
      let onTime = 0;
      let total = 0;

      let latestShift = null;
      const detailedShifts = [];

      empAppointments?.forEach((appt) => {
        const shiftDay = moment(appt?.appointmentDateTime).startOf("day");

        if (!latestShift || shiftDay.isAfter(latestShift?.date)) {
          latestShift = {
            date: shiftDay,
            site: clientMap[appt?.clientId] || "Unknown",
          };
        }

        const details = appt?.apoointmentDetails || {};

        let shiftEnd = null;
        
        if (details.endTime && appt.appointmentDateTime) {
             const baseDate = moment(appt.appointmentDateTime);
             const timeStr = details.endTime; 
             const dateStr = baseDate.format("YYYY-MM-DD");
             shiftEnd = moment(`${dateStr} ${timeStr}`, "YYYY-MM-DD hh:mm a");
             
             if (shiftEnd.isValid() && shiftEnd.isBefore(baseDate)) {
                 shiftEnd.add(1, 'day');
             }
        } else if (appt.appointmentDateTime) {
            shiftEnd = moment(appt.appointmentDateTime).add(details.duration || 180, 'minutes');
        }

        let requiredCount = 0;
        let providedCount = 0;
        let lateCount = 0;
        let hasMissing = false;
        let hasInvalid = false;
        let hasLate = false;
        const collectedPhotos = [];

        Object.values(appt?.tasks || {})?.forEach((group) => {
          (group?.tasksList || [])?.forEach((task) => {
            if (task.pos === true) {
              requiredCount++;

              const proofs = task?.proofs || [];

              if (proofs?.length === 0) {
                hasMissing = true;
              } else {
                providedCount++;

                const validProofs = proofs.filter(p => p.verified === true && p.source === 'camera');
                
                if (validProofs.length === 0) {
                  hasInvalid = true;
                } else if (shiftEnd) {
                  const isLate = proofs.some((proof) => {
                    const ts = proof?.captured_at_device;
                    if (!ts) return false;
                    const proofTime = typeof ts === "object" && ts.seconds ? moment.unix(ts.seconds) : moment(ts);
                    return proofTime.isValid() && proofTime.isAfter(shiftEnd);
                  });

                  if (isLate) hasLate = true;
                }

                proofs?.forEach((proof) => {
                  const url = proof.url || "";
                  if (url) {
                    collectedPhotos.push({
                      url: url,
                      verified: proof.verified === true,
                      gps: proof.gps,
                      capturedAt: proof.captured_at_device
                    });
                  }
                });
              }
            }
          });
        });

        if (requiredCount === 0) return;

        total++;

        let shiftStatus = "complete";
        let statusText = "On Time";
        let locationMessage = requiredCount > 0 ? "Required" : "Not Required";

        if (requiredCount > 0) {
          if (hasMissing) {
            locationMessage = "Missing Proof";
          } else if (hasInvalid) {
            locationMessage = "Not Verified";
          } else {
            locationMessage = "Verified via geofencing";
          }
        }

        // Priority: Missing/Invalid > Late > Compliant
        if (hasMissing) {
          missing++;
          shiftStatus = "missing";
          statusText = "Missing Proof";
        } else if (hasInvalid) {
          invalid++;
          shiftStatus = "missing";
          statusText = "Not Verified";
        } else if (hasLate) {
          late++;
          shiftStatus = "late";
          statusText = "Late Proof";
        } else {
          onTime++;
          statusText = "Verified";
        }

        detailedShifts.push({
          date: shiftDay.format("MMM DD, YYYY"),
          shiftName: details?.shiftName || "Shift",
          site: clientMap[appt.clientId] || "Unknown",
          startTime: details?.time || "-",
          endTime: details?.endTime || "-",
          status: shiftStatus,
          duration: details?.duration || "-",
          statusText,
          requiredProofTasks: requiredCount,
          providedProofTasks: providedCount,
          lateProofTasks: lateCount,
          photoCount: collectedPhotos.length,
          photos: collectedPhotos,
          location: locationMessage,
        });
      });

      const completionRate = total > 0 ? Math.round((onTime / total) * 100) : 0;
      const riskScore = missing + invalid + late * 0.5;

      return {
        id: emp.id,
        name: `${emp?.firstName || ""} ${emp?.lastName || ""}`.trim(),
        email: emp.email,
        phone: emp.phoneNumber,
        photo: emp.photo,
        completionRate,
        missingProofs: missing,
        invalidProofs: invalid,
        lateProofs: late,
        completed: onTime,
        totalShifts: total,
        lastShift: latestShift?.date?.format("MMM DD, YYYY") || "-",
        site: latestShift?.site || "Unknown",
        riskScore,
        status: missing > 0 ? "missing" : late > 0 ? "late" : "complete",
        detailedShifts: detailedShifts.sort(
          (a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()
        ),
      };
    });

    return result?.filter(emp => emp?.detailedShifts?.length > 0)?.sort((a, b) => b?.riskScore - a?.riskScore)?.slice(0, 5);
  }, [employees, rawAppointments, clients, dateRange]);

  const handleEmployeeRowClick = (employee) => {
    setSelectedEmployee(employee);
    setEmployeeHistoryTimeFrame('last7');
    setShowEmployeeDetailModal(true);
  };

  const filteredEmployees = useMemo(() => {
    const list = employeeRiskData;

    if (!Array.isArray(list)) return [];

    return list?.filter((emp) => {
        
      const name = emp?.name || emp?.email?.split("@")[0] || "";
      const status = emp?.missingProofs > 0 ? "missing" : emp?.lateProofs > 0 ? "late" : "complete";
      const matchesSearch = name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) || emp?.site?.toLowerCase().includes(employeeSearchTerm.toLowerCase());
      const matchesStatus = employeeStatusFilter === "all" || status === employeeStatusFilter;
     return matchesSearch && matchesStatus;
    });
  }, [employeeSearchTerm, employeeStatusFilter, employeeRiskData]);

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployeeIds((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId],
    );
  };

  const toggleSelectAll = () => {
    if (selectedEmployeeIds?.length === filteredEmployees?.length) {
      setSelectedEmployeeIds([]);
    } else {
      setSelectedEmployeeIds(filteredEmployees?.map((e) => e?.id));
    }
  };

  const handleSendReminder = (employeeIds) => {
    setMessageRecipients(employeeIds);
    setShowMessageModal(true);
  };

  return (
    <>
    <Card className="shadow-md bg-white dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
      <CardHeader className="p-16">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="text-xl font-bold">
              Employee Performance
            </CardTitle>
            <CardDescription>
              View and manage employee proof submission records
            </CardDescription>
          </div>
          {selectedEmployeeIds?.length > 0 && (
            <Button
              onClick={() => handleSendReminder(selectedEmployeeIds)}
              className="w-full sm:w-auto"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Reminder ({selectedEmployeeIds?.length})
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-16">
        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Label htmlFor="employee-search" className="sr-only">
              Search employees by name or site
            </Label>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="employee-search"
              placeholder="Search by name or site..."
              value={employeeSearchTerm}
              onChange={(e) => setEmployeeSearchTerm(e.target.value)}
              className="pl-40"
              aria-describedby="employee-count"
            />
            <span id="employee-count" className="sr-only">
              {filteredEmployees?.length} employee
              {filteredEmployees?.length !== 1 ? "s" : ""} found
            </span>
          </div>
          <div className="w-full sm:w-[180px]">
            <Label htmlFor="status-filter" className="sr-only">
              Filter by proof status
            </Label>
            <SingleSelectNew
              contentClass="bg-white text-black border-gray-300 dark:bg-[#2c3440] dark:text-white dark:border-[#464c59]"
              itemClass="hover:bg-gray-100 dark:hover:bg-[#3c4351]"
              value={employeeStatusFilter}
              options={statusOptions}
              name="statusfilter"
              label="Status"
              onChange={(e) => setEmployeeStatusFilter(e)}
            />
          </div>
        </div>

        {isMobile ? (
          <div className="space-y-3">
            {filteredEmployees?.length > 0 && (
              <Card className="bg-muted/30 bg-white dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
                <CardContent className="p-16">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={
                        selectedEmployeeIds?.length ===
                        filteredEmployees?.length
                      }
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all employees"
                      className="h-5 w-5"
                    />
                    <span className="text-sm font-medium">
                      {selectedEmployeeIds?.length > 0
                        ? `${selectedEmployeeIds?.length} selected`
                        : "Select all employees"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {filteredEmployees?.length === 0 ? (
              <Card className="bg-white dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
                <CardContent className="p-12">
                  <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground py-6">
                    <Users
                      className={cn(
                        "opacity-50",
                        isMobile ? "h-16 w-16" : "h-12 w-12",
                      )}
                    />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">No employees found</p>
                      <p className="text-xs leading-relaxed">
                        Try adjusting your search or filters
                      </p>
                    </div>
                    {(employeeSearchTerm || employeeStatusFilter !== "all") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEmployeeSearchTerm("");
                          setEmployeeStatusFilter("all");
                        }}
                        className="mt-8"
                      >
                        Clear filters
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              filteredEmployees?.map((emp, idx) => (
                <Card
                  key={emp.id}
                  className="cursor-pointer hover:shadow-md transition-all animate-in fade-in border-l-4 bg-white dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]"
                  style={{
                    animationDelay: `${idx * 30}ms`,
                    borderLeftColor:
                      emp.completionRate >= 85
                        ? "#10b981"
                        : emp.completionRate >= 70
                          ? "#f59e0b"
                          : "#ef4444",
                  }}
                  onClick={() => handleEmployeeRowClick(emp)}
                >
                  <CardContent className="p-16">
                    <div className="flex items-start gap-3">
                      <div
                        className="pt-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selectedEmployeeIds.includes(emp.id)}
                          onCheckedChange={() =>
                            toggleEmployeeSelection(emp.id)
                          }
                          aria-label={`Select ${emp.name}`}
                          className="h-5 w-5"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-8">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-base truncate">
                              {emp?.name}
                            </h4>
                            <p className="text-sm text-muted-foreground truncate">
                              {emp?.site}
                            </p>
                          </div>
                          <Badge
                            variant={
                              emp?.completionRate >= 85
                                ? "default"
                                : emp?.completionRate >= 70
                                  ? "secondary"
                                  : "destructive"
                            }
                            className="shrink-0 text-sm font-semibold px-3 py-1"
                          >
                            {emp?.completionRate}%
                          </Badge>
                        </div>

                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">
                              Missing:
                            </span>
                            <span
                              className={cn(
                                "font-semibold",
                                emp.missingProofs > 0 &&
                                  "text-red-600 dark:text-red-400",
                              )}
                            >
                              {emp?.missingProofs}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">Late:</span>
                            <span
                              className={cn(
                                "font-semibold",
                                emp?.lateProofs > 0 &&
                                  "text-orange-600 dark:text-orange-400",
                              )}
                            >
                              {emp?.lateProofs}
                            </span>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={
                        selectedEmployeeIds?.length ===
                          filteredEmployees?.length &&
                        filteredEmployees?.length > 0
                      }
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all employees"
                    />
                  </TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead className="hidden md:table-cell">Site</TableHead>
                  <TableHead className="text-center">Rate</TableHead>
                  <TableHead className="text-center hidden sm:table-cell">
                    Missing
                  </TableHead>
                  <TableHead className="text-center hidden sm:table-cell">
                    Late
                  </TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-white dark:bg-[#1A1F2C]">
                {filteredEmployees?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-40 text-center bg-white dark:bg-[#1A1F2C]">
                      <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground py-6">
                        <Users className="h-12 w-12 opacity-50" />
                        <div className="space-y-2">
                          <p className="text-sm font-medium">
                            No employees found
                          </p>
                          <p className="text-xs leading-relaxed">
                            Try adjusting your search or filters
                          </p>
                        </div>
                        {(employeeSearchTerm ||
                          employeeStatusFilter !== "all") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEmployeeSearchTerm("");
                              setEmployeeStatusFilter("all");
                            }}
                            className="mt-2"
                          >
                            Clear filters
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees?.map((emp, idx) => (
                    <TableRow
                      key={emp?.id}
                      className="cursor-pointer transition-colors animate-in fade-in bg-white dark:bg-[#1A1F2C]"
                      onClick={() => handleEmployeeRowClick(emp)}
                      style={{ animationDelay: `${idx * 30}ms` }}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedEmployeeIds.includes(emp.id)}
                          onCheckedChange={() =>
                            toggleEmployeeSelection(emp.id)
                          }
                          aria-label={`Select ${emp.name}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate">
                            {emp?.name}
                          </p>
                          <p className="text-xs text-muted-foreground md:hidden truncate">
                            {emp?.site}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className="text-sm text-muted-foreground">
                          {emp?.site}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            emp?.completionRate >= 85
                              ? "default"
                              : emp?.completionRate >= 70
                                ? "secondary"
                                : "destructive"
                          }
                        >
                          {emp?.completionRate}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center hidden sm:table-cell">
                        <span
                          className={cn(
                            "text-sm font-medium",
                            emp?.missingProofs > 0 &&
                              "text-red-600 dark:text-red-400",
                          )}
                        >
                          {emp?.missingProofs}
                        </span>
                      </TableCell>
                      <TableCell className="text-center hidden sm:table-cell">
                        <span
                          className={cn(
                            "text-sm font-medium",
                            emp?.lateProofs > 0 &&
                              "text-orange-600 dark:text-orange-400",
                          )}
                        >
                          {emp?.lateProofs}
                        </span>
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
};

export default EmployeeTab;
